import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadSimple, Sparkle, CheckCircle, X, LinkSimple, Plus, ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import AuthorsEditor from "@/components/marketing/AuthorsEditor";

/**
 * Multi-step Add Item wizard.
 * Steps: 1) Classification + Title 2) Author + Professionals + Reviewers
 *        3) Upload files 4) AI Review 5) Campaign details + Submit
 */
export default function MarketingWizard({ open, onOpenChange, choices, onCreated }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [item, setItem] = useState(null);          // becomes set after step 1 (creates the row)
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);

  // Step-1 fields
  const [title, setTitle] = useState("");
  const [classification, setClassification] = useState("Article");
  const [description, setDescription] = useState("");
  const [providedBy, setProvidedBy] = useState("Author");

  // Step-2 fields
  const [authors, setAuthors] = useState([{ name: user?.name || "", email: user?.email || "", department: "" }]);
  const [professionals, setProfessionals] = useState([]);
  const [reviewers, setReviewers] = useState([]);

  // Step-5 fields (campaign + links + publishing)
  const [campaignDesc, setCampaignDesc] = useState("");
  const [campaignGoal, setCampaignGoal] = useState("");
  const [campaignEnd, setCampaignEnd] = useState("");
  const [links, setLinks] = useState([]);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [linkedinAt, setLinkedinAt] = useState("");

  // Step-1 extra
  const [receivedAt, setReceivedAt] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/users").then((r) => setStaff(r.data || [])).catch(() => {});
    // Pre-fill first author from current user
    setAuthors([{ name: user?.name || "", email: user?.email || "", department: "" }]);
  }, [open, user]);

  const reset = () => {
    setStep(1); setItem(null); setBusy(false);
    setTitle(""); setClassification("Article"); setDescription(""); setProvidedBy("Author");
    setAuthors([{ name: user?.name || "", email: user?.email || "", department: "" }]);
    setProfessionals([]); setReviewers([]);
    setCampaignDesc(""); setCampaignGoal(""); setCampaignEnd(""); setLinks([]);
    setWebsiteUrl(""); setLinkedinAt(""); setReceivedAt("");
  };

  useEffect(() => { if (!open) reset(); /* eslint-disable-next-line */ }, [open]);

  const cleanAuthors = () => authors.filter((a) => (a.name || "").trim() || (a.email || "").trim());

  const createDraft = async () => {
    if (!title.trim()) return toast.error("Title is required");
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/items", {
        title: title.trim(), classification, description: description.trim() || null,
        provided_by: providedBy, authors: cleanAuthors(),
        professional_emails: professionals, reviewer_emails: reviewers, links,
        campaign_description: campaignDesc || null, campaign_goal: campaignGoal || null,
        campaign_end_at: campaignEnd || null, received_at: receivedAt || null,
      });
      setItem(data);
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create draft");
    } finally {
      setBusy(false);
    }
  };

  const savePeople = async () => {
    if (!item) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/marketing/items/${item.id}`, {
        authors: cleanAuthors(),
        professional_emails: professionals, reviewer_emails: reviewers,
      });
      setItem(data);
      setStep(3);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save people");
    } finally {
      setBusy(false);
    }
  };

  const uploadFile = async (file) => {
    if (!item || !file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/marketing/items/${item.id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItem(data);
      toast.success("Uploaded to OneDrive.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const runAI = async () => {
    if (!item) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/marketing/items/${item.id}/ai-review`);
      setItem(data);
      toast.success(`AI score: ${data.ai_insight?.score}/100`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI review failed");
    } finally {
      setBusy(false);
    }
  };

  const submitForReview = async () => {
    if (!item) return;
    if (reviewers.length === 0) return toast.error("Pick at least one reviewer");
    setBusy(true);
    try {
      // Save campaign fields + links before submitting
      await api.patch(`/marketing/items/${item.id}`, {
        campaign_description: campaignDesc || null, campaign_goal: campaignGoal || null,
        campaign_end_at: campaignEnd || null, links,
        website_url: websiteUrl || "", linkedin_posted_at: linkedinAt || "",
        received_at: receivedAt || "",
        reviewer_emails: reviewers,
      });
      await api.post(`/marketing/items/${item.id}/submit-review`, { reviewer_emails: reviewers });
      toast.success("Submitted for review.");
      onCreated?.(item);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  const toggleEmail = (arr, setArr, val) => setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const addLink = () => setLinks((prev) => [...prev, { label: "", url: "" }]);
  const patchLink = (i, patch) => setLinks((prev) => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const rmLink = (i) => setLinks((prev) => prev.filter((_, idx) => idx !== i));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="marketing-wizard">
        <DialogHeader>
          <DialogTitle>Add marketing item — step {step} of 5</DialogTitle>
        </DialogHeader>

        <div className="mb-4 flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <div key={n} className={`h-1 flex-1 rounded-sm ${step >= n ? "bg-slate-900" : "bg-slate-200"}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4" data-testid="wizard-step-1">
            <div>
              <Label className="label-tracking">Classification</Label>
              <Select value={classification} onValueChange={setClassification}>
                <SelectTrigger data-testid="wizard-classification" className="mt-1 rounded-sm h-10"><SelectValue/></SelectTrigger>
                <SelectContent>{(choices?.classifications || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tracking">Title</Label>
              <Input data-testid="wizard-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 rounded-sm h-10" placeholder="e.g. Understanding the new tax act"/>
            </div>
            <div>
              <Label className="label-tracking">Description / summary</Label>
              <Textarea data-testid="wizard-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} className="mt-1 rounded-sm" placeholder="Short body / synopsis. AI review uses this text."/>
            </div>
            <div>
              <Label className="label-tracking">Provided by</Label>
              <Select value={providedBy} onValueChange={setProvidedBy}>
                <SelectTrigger data-testid="wizard-provided-by" className="mt-1 rounded-sm h-10"><SelectValue/></SelectTrigger>
                <SelectContent>{(choices?.provided_by_options || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tracking">Date received / sent to professionals</Label>
              <Input data-testid="wizard-received-at" type="date" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)} className="mt-1 rounded-sm h-10"/>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="wizard-step-2">
            <div>
              <Label className="label-tracking">Authors</Label>
              <p className="text-xs text-slate-500 mt-0.5 mb-2">Add every author — an article can have multiple (e.g. three seniors). The first author is the primary.</p>
              <AuthorsEditor value={authors} onChange={setAuthors} staff={staff} testid="wizard-authors"/>
            </div>
            <PeoplePicker label="Professionals mentioned" staff={staff} value={professionals} onChange={setProfessionals} testid="wizard-professionals"/>
            <PeoplePicker label="Reviewers (staff + guests)" staff={staff} value={reviewers} onChange={setReviewers} includeGuests testid="wizard-reviewers"/>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="wizard-step-3">
            <p className="text-sm text-slate-600">Upload the article, podcast, or video. Files stream to the Leads@ OneDrive — no size limit.</p>
            <label className="flex items-center justify-center border-2 border-dashed border-slate-300 rounded-sm p-8 cursor-pointer hover:border-slate-900" data-testid="wizard-upload-dropzone">
              <input type="file" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="wizard-upload-input"/>
              <div className="text-center">
                <UploadSimple size={28} className="mx-auto text-slate-400" />
                <div className="mt-2 text-sm text-slate-700 font-medium">Click to upload</div>
                <div className="text-xs text-slate-500">PDF, DOCX, MP3, MP4 — any size</div>
              </div>
            </label>
            {item?.attachments?.length > 0 && (
              <ul className="space-y-1" data-testid="wizard-uploads">
                {item.attachments.map((a) => (
                  <li key={a.id} className="text-xs bg-slate-50 border border-slate-200 rounded-sm p-2 flex items-center justify-between gap-2">
                    <span className="truncate">{a.filename} <span className="text-slate-400">({(a.size / 1024).toFixed(0)} KB)</span></span>
                    {a.onedrive_web_url && <a href={a.onedrive_web_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Open</a>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4" data-testid="wizard-step-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="font-display text-lg text-slate-900">AI editorial review</h4>
                <p className="text-xs text-slate-500 mt-1">Claude Opus 4.7 scans your title + description + campaign fields for factual accuracy and gives improvement bullets. It will NOT invent laws or case citations — anything unverifiable is flagged.</p>
              </div>
              <Button data-testid="wizard-run-ai" onClick={runAI} disabled={busy} className="rounded-sm bg-purple-600 hover:bg-purple-700 gap-2 shrink-0">
                <Sparkle size={14} weight="fill" /> {busy ? "Analyzing…" : "Run AI review"}
              </Button>
            </div>
            {item?.ai_insight && (
              <div className="border border-slate-200 rounded-sm p-4 bg-slate-50 space-y-3" data-testid="wizard-ai-result">
                <div className="flex items-center gap-3">
                  <div className="text-4xl font-display text-slate-900" data-testid="wizard-ai-score">{item.ai_insight.score}</div>
                  <div className="text-xs text-slate-600">/100 factual+editorial score</div>
                </div>
                {item.ai_insight.insights?.length > 0 && (
                  <div>
                    <div className="label-tracking mb-1">Insights</div>
                    <ul className="text-sm text-slate-700 space-y-1 list-disc pl-5">
                      {item.ai_insight.insights.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {item.ai_insight.unverified_claims?.length > 0 && (
                  <div>
                    <div className="label-tracking mb-1 text-amber-700">Unverified claims — please double-check</div>
                    <ul className="text-sm text-amber-900 space-y-1 list-disc pl-5">
                      {item.ai_insight.unverified_claims.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {item.ai_insight.factual_issues?.length > 0 && (
                  <div>
                    <div className="label-tracking mb-1 text-rose-700">Factual issues</div>
                    <ul className="text-sm text-rose-900 space-y-1 list-disc pl-5">
                      {item.ai_insight.factual_issues.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4" data-testid="wizard-step-5">
            <div>
              <Label className="label-tracking">Campaign description</Label>
              <Textarea data-testid="wizard-campaign-desc" value={campaignDesc} onChange={(e) => setCampaignDesc(e.target.value)} rows={3} className="mt-1 rounded-sm"/>
            </div>
            <div>
              <Label className="label-tracking">Goal</Label>
              <Textarea data-testid="wizard-campaign-goal" value={campaignGoal} onChange={(e) => setCampaignGoal(e.target.value)} rows={2} className="mt-1 rounded-sm"/>
            </div>
            <div>
              <Label className="label-tracking">Campaign end date</Label>
              <Input data-testid="wizard-campaign-end" type="date" value={campaignEnd} onChange={(e) => setCampaignEnd(e.target.value)} className="mt-1 rounded-sm h-10"/>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="label-tracking">Website link</Label>
                <Input data-testid="wizard-website-url" type="url" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="https://barnardinc.co.za/…" className="mt-1 rounded-sm h-10"/>
              </div>
              <div>
                <Label className="label-tracking">LinkedIn posted date</Label>
                <Input data-testid="wizard-linkedin-at" type="date" value={linkedinAt} onChange={(e) => setLinkedinAt(e.target.value)} className="mt-1 rounded-sm h-10"/>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="label-tracking">External links</Label>
                <button data-testid="wizard-add-link" type="button" onClick={addLink} className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">
                  <Plus size={12}/> Add link
                </button>
              </div>
              {links.length === 0 ? (
                <p className="text-xs text-slate-400 italic mt-1">No links.</p>
              ) : (
                <div className="space-y-2 mt-2">
                  {links.map((l, i) => (
                    <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center">
                      <Input placeholder="Label" value={l.label} onChange={(e) => patchLink(i, { label: e.target.value })} className="rounded-sm h-9"/>
                      <Input placeholder="https://" value={l.url} onChange={(e) => patchLink(i, { url: e.target.value })} className="rounded-sm h-9"/>
                      <button type="button" onClick={() => rmLink(i)} className="text-slate-400 hover:text-rose-600"><X size={14}/></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-2">
              Once submitted, the item moves to <b>In Review / Author</b> and reviewers can approve or reject.
            </p>
          </div>
        )}

        <DialogFooter className="pt-4 flex-row justify-between">
          <div>
            {step > 1 && step < 5 && (
              <Button type="button" variant="outline" onClick={() => setStep((s) => s - 1)} data-testid="wizard-back" className="rounded-sm gap-1">
                <ArrowLeft size={14}/> Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            {step === 1 && <Button data-testid="wizard-next-1" onClick={createDraft} disabled={busy || !title.trim()} className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-1">Next <ArrowRight size={14}/></Button>}
            {step === 2 && <Button data-testid="wizard-next-2" onClick={savePeople} disabled={busy || cleanAuthors().length === 0} className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-1">Next <ArrowRight size={14}/></Button>}
            {step === 3 && <Button data-testid="wizard-next-3" onClick={() => setStep(4)} className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-1">Next <ArrowRight size={14}/></Button>}
            {step === 4 && <Button data-testid="wizard-next-4" onClick={() => setStep(5)} className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-1">Next <ArrowRight size={14}/></Button>}
            {step === 5 && <Button data-testid="wizard-submit" onClick={submitForReview} disabled={busy} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 gap-1"><CheckCircle size={14} weight="fill"/> Submit for review</Button>}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PeoplePicker({ label, staff, value, onChange, includeGuests = false, testid }) {
  const [query, setQuery] = useState("");
  const list = (staff || [])
    .filter((u) => (includeGuests ? true : u.role !== "guest"))
    .filter((u) => u.status === "active")
    .filter((u) => !query || `${u.name} ${u.email}`.toLowerCase().includes(query.toLowerCase()));
  return (
    <div>
      <Label className="label-tracking">{label}</Label>
      <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter…" className="mt-1 rounded-sm h-9 text-sm mb-2" data-testid={`${testid}-search`}/>
      <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-sm p-2 space-y-1">
        {list.length === 0 ? (
          <div className="text-xs text-slate-400 italic p-1">No users.</div>
        ) : list.map((u) => (
          <label key={u.id} className="flex items-center gap-2 text-xs px-2 py-1 rounded-sm hover:bg-slate-50 cursor-pointer">
            <input type="checkbox" checked={value.includes(u.email)} onChange={() => onChange(value.includes(u.email) ? value.filter((e) => e !== u.email) : [...value, u.email])} data-testid={`${testid}-${u.email}`}/>
            <span className="font-medium text-slate-900">{u.name || u.email.split("@")[0]}</span>
            <span className="text-slate-400 font-mono">{u.email}</span>
            {u.role === "guest" && <span className="ml-auto px-1 py-0.5 rounded-sm bg-amber-100 text-amber-800 text-[10px]">Guest</span>}
            {u.department && u.role !== "guest" && <span className="ml-auto text-slate-500">{u.department}</span>}
          </label>
        ))}
      </div>
      {value.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {value.map((e) => (
            <span key={e} className="px-2 py-0.5 rounded-sm bg-slate-100 text-slate-700 text-[10px] font-mono flex items-center gap-1">
              {e}
              <button type="button" onClick={() => onChange(value.filter((x) => x !== e))} className="hover:text-rose-600"><X size={10}/></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
